import {
  CLASSES,
  ClassIdSchema,
  type CollisionGrid,
  getCompanion,
  stepIntervalMs,
  TILE_SIZE,
  type TilePosition,
  walkSpeedMultiplier,
} from '@shefira/shared';

import { MovementPrediction } from '../net/movement-prediction';

/** What the synchronised state says of the local player, as far as walking goes. */
export interface LocalPlayerState extends TilePosition {
  dir: string;
  anim: string;
  classId: string;
  /** Companion id of the mount ridden, empty on foot. */
  mount: string;
  /** Walking speed from the boots, whole percent (ADR 0059). */
  speedBonus: number;
  hp: number;
}

/** How to draw the local player this frame. */
export interface LocalDrawing {
  /** Feet position in whole pixels (rule 6). */
  px: number;
  py: number;
  anim: string;
  dir: string;
}

/**
 * The world scene's side of the predicted walk (ADR 0045): turns the
 * synchronised player into what `MovementPrediction` needs, and its answer
 * into pixels, an animation and a facing. Other players and monsters keep
 * the scene's plain interpolation.
 */
export class LocalMovement {
  private readonly prediction = new MovementPrediction();

  /** Where a new walk starts: the predicted tile, not the server's older one. */
  get tile(): TilePosition | undefined {
    return this.prediction.tile;
  }

  /** Where a click turns the walk, sent with `move_to` (undefined when the server leads). */
  get turningTile(): TilePosition | undefined {
    return this.prediction.turningTile;
  }

  /** Where the walk the player leads goes (undefined when the server leads). */
  get target(): TilePosition | undefined {
    return this.prediction.target;
  }

  /** Tiles the predicted walk has still to enter. */
  get stepsLeft(): number {
    return this.prediction.stepsLeft;
  }

  /** Validate and start a walk; true when `move_to` should go out. */
  walk(grid: CollisionGrid, target: TilePosition, now: number): boolean {
    return this.prediction.walk(grid, target, now).ok;
  }

  /** The server takes over the walk (attack, pickup) or ends it (death). */
  stop(): void {
    this.prediction.stop();
  }

  /** The server moved the player at once (a charge, ADR 0048): stand there now, walk nowhere. */
  snap(tile: TilePosition): void {
    this.prediction.snap(tile);
  }

  reject(server: TilePosition, now: number): void {
    this.prediction.reject(server, now);
  }

  frame(now: number, player: LocalPlayerState): LocalDrawing {
    // A body on the ground walks nowhere, whatever the last click said.
    if (player.hp <= 0) this.prediction.stop();
    const drawn = this.prediction.update(
      now,
      { x: player.x, y: player.y, walking: player.anim === 'walk' },
      this.intervalOf(player),
    );
    return {
      px: Math.round(drawn.x * TILE_SIZE + TILE_SIZE / 2),
      py: Math.round(drawn.y * TILE_SIZE + TILE_SIZE),
      ...this.pose(player, drawn.moving, drawn.facing),
    };
  }

  /** The server's cadence for this body: the class's pace, times the mount's if ridden and the boots'. */
  private intervalOf(player: LocalPlayerState): number {
    const parsed = ClassIdSchema.safeParse(player.classId);
    const definition = CLASSES[parsed.success ? parsed.data : 'warrior'];
    const mount = player.mount === '' ? undefined : getCompanion(player.mount);
    return stepIntervalMs(
      definition,
      walkSpeedMultiplier(mount?.speedMultiplier ?? 1, player.speedBonus),
    );
  }

  /**
   * The walk cycle and the facing follow the prediction while it walks; a
   * one-shot the server plays (a blow, a fall) and the facing of a standing
   * player stay the server's. Arrived before the server, the body stands.
   */
  private pose(
    player: LocalPlayerState,
    moving: boolean,
    facing: string | null,
  ): { anim: string; dir: string } {
    if (player.anim === 'death') return { anim: player.anim, dir: player.dir };
    if (moving) return { anim: 'walk', dir: facing ?? player.dir };
    if (player.anim === 'walk') return { anim: 'idle', dir: facing ?? player.dir };
    return { anim: player.anim, dir: player.dir };
  }
}
